<template>
    <ai-message-vetting-agent-details :agentId="props.agentId">
        <form-wrapper
            :sections="[
                { id: 'name', label: 'Name' },
                { id: 'description', label: 'Description' },
                { id: 'instructions', label: 'Instructions' },
                { id: 'validation', label: 'Validation' },
            ]"
            :hideNav="Boolean(props.agentId)"
        >
            <vx-expandable-card id="name">
                <template v-slot:header>Name</template>
                <template v-slot:content>
                    <input-with-errors :errors="validation.name">
                        <vx-input data-test="name" autofocus v-model="agent.name" size="lg"></vx-input>
                    </input-with-errors>
                </template>
            </vx-expandable-card>

            <vx-expandable-card id="description">
                <template v-slot:header>Description</template>
                <template v-slot:content>
                    <input-with-errors :errors="validation.description">
                        <vx-input
                            data-test="description"
                            v-model="agent.description"
                            size="lg"
                            :rows="4"
                            counter
                            :maxlength="280"
                        ></vx-input>
                    </input-with-errors>
                </template>
            </vx-expandable-card>

            <vx-expandable-card id="instructions">
                <template v-slot:header>Instructions</template>
                <template v-slot:content>
                    <input-with-errors :errors="validation.instructions">
                        <div>
                            <vx-input
                                data-test="instructions"
                                v-model="agent.instructions"
                                size="lg"
                                :rows="4"
                                counter
                                autoresize
                                maxHeight="none"
                                :maxlength="3000"
                            ></vx-input>
                        </div>
                    </input-with-errors>
                    <vx-button
                        v-if="!showAiSuggestions"
                        type="button"
                        color="muted-light"
                        size="md"
                        class="mt-3 mix-blend-multiply"
                        data-test="improve-instructions"
                        @click="openAiSuggestions"
                        :disabled="!agent.instructions"
                    >
                        Improve Instructions
                        <vx-badge size="2xs" color="slate">
                            AI <font-awesome-icon widthAuto :icon="faSparkle" />
                        </vx-badge>
                    </vx-button>

                    <ai-suggestions
                        v-if="showAiSuggestions"
                        class="mt-3"
                        :loading="aiSuggestionsLoading"
                        :suggestions="aiInstructionSuggestions"
                        @select="selectSuggestion"
                        @generateMore="loadAiSuggestions"
                        @close="closeAiSuggestions"
                    />
                </template>
            </vx-expandable-card>

            <vx-expandable-card id="validation">
                <template v-slot:header>Validation</template>
                <template v-slot:content>
                    <div class="flex flex-col items-start mb-2">
                        <strong class="text-slate-900 text-sm font-semibold sm:text-base sm:leading-6">
                            How should the agent handle validation?
                        </strong>
                    </div>

                    <vx-radio-selection v-model="agent.validation" data-test="validation-selection">
                        <vx-radio-selection-item value="warn" data-test="validation-warn">
                            <template v-slot:header>Warn</template>
                            <template v-slot:content>
                                <ul class="list-disc list-outside ml-10 lg:ml-4">
                                    <li>a prominent warning message will displayed to the user</li>
                                </ul>
                            </template>
                        </vx-radio-selection-item>
                        <vx-radio-selection-item value="block" data-test="validation-block">
                            <template v-slot:header>Block</template>
                            <template v-slot:content>
                                <ul class="list-disc list-outside ml-10 lg:ml-4">
                                    <li>a prominent error message will displayed to the user</li>
                                    <li>the user will be blocked from sending</li>
                                    <li class="font-semibold">use sparingly</li>
                                </ul>
                            </template>
                        </vx-radio-selection-item>
                    </vx-radio-selection>
                </template>
            </vx-expandable-card>

            <vx-sticky-footer>
                <div class="flex flex-col-reverse gap-2 w-full grow lg:flex-row">
                    <vx-button
                        :href="listPageUrl"
                        block
                        class="grow lg:w-20"
                        size="lg"
                        color="muted"
                        data-test="cancel-button"
                    >
                        Cancel
                    </vx-button>
                    <div class="flex flex-col-reverse lg:grid lg:grid-cols-2 gap-2 w-full">
                        <vx-button @click="openPreviewModal" size="lg" block color="muted" data-test="preview-button">
                            Preview
                        </vx-button>
                        <vx-button
                            @click="submit()"
                            data-test="submit-button"
                            block
                            size="lg"
                            color="primary"
                            :loading="processing"
                        >
                            Save
                        </vx-button>
                    </div>
                </div>
            </vx-sticky-footer>

            <ai-message-vetting-agent-preview-modal
                v-model:visible="previewModalVisible"
                :instructions="agent.instructions"
                :validation="agent.validation"
            />
        </form-wrapper>
    </ai-message-vetting-agent-details>
</template>

<script setup lang="ts">
import AiSuggestions from '~/components/general/AiSuggestions.vue';
import AiMessageVettingAgentDetails from './AiMessageVettingAgentDetails.vue';
import AiMessageVettingAgentPreviewModal from './AiMessageVettingAgentPreviewModal.vue';
import InputWithErrors from '~/components/general/InputWithErrors.vue';
import {
    VxButton,
    VxStickyFooter,
    VxExpandableCard,
    VxInput,
    VxRadioSelection,
    VxRadioSelectionItem,
    VxBadge,
} from '@voxie/frontend-components';
import { faSparkle, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { ref } from 'vue';
import { required, maxLength } from '@vuelidate/validators';
import { groupAndOrgUnitName } from '~/utils/validation';
import { aiMessageVettingAgentsClient } from '~/services';
import FormWrapper from '~/components/general/FormWrapper.vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { useAiMessageVettingAgentDetails } from '~/composables/useAiMessageVettingAgentDetails';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import { isCancel } from 'axios';

const teamId = Spark.state.currentTeam.id;

const props = defineProps<{
    agentId?: number;
}>();

const listPageUrl = '/settings/ai-agents#/ai-message-vetting-agents';

const { agent } = useAiMessageVettingAgentDetails();
const toasted = useToasted();

const showAiSuggestions = ref(false);
const aiInstructionSuggestions = ref<string[]>([]);
const aiSuggestionsLoading = ref(false);
const aiSuggestionsCancelKey = Symbol('ai-suggestions-request');
const previewModalVisible = ref(false);

const openPreviewModal = () => {
    if (!agent.value.instructions) {
        toasted.error('Enter instructions before previewing.');
        return;
    }

    previewModalVisible.value = true;
};

const loadAiSuggestions = async () => {
    if (aiSuggestionsLoading.value) {
        return;
    }

    aiSuggestionsLoading.value = true;
    scrollToInstructionsCardIfNeeded();

    try {
        const response = await aiMessageVettingAgentsClient.getInstructionsSuggestions(
            { teamId },
            { instructions: agent.value.instructions },
            { cancelKey: aiSuggestionsCancelKey }
        );
        aiInstructionSuggestions.value = response.data.data;
    } catch (error) {
        if (!isCancel(error)) {
            toasted.error(catchRequestError(error));
            closeAiSuggestions();
        }
    } finally {
        aiSuggestionsLoading.value = false;
    }
};

const openAiSuggestions = async () => {
    const isValid = await validation.value.instructions.$validate();
    if (!isValid) {
        return;
    }

    showAiSuggestions.value = true;
    loadAiSuggestions();
};

const closeAiSuggestions = () => {
    aiMessageVettingAgentsClient.cancelRequest({ teamId }, aiSuggestionsCancelKey);
    showAiSuggestions.value = false;
    aiInstructionSuggestions.value = [];
};

const selectSuggestion = (text: string) => {
    agent.value.instructions = text;
    closeAiSuggestions();
};

const recentlyCreatedId = ref<number>();

const save = async () => {
    if (!props.agentId) {
        const response = await aiMessageVettingAgentsClient.createAiMessageVettingAgent({ teamId }, agent.value);
        recentlyCreatedId.value = response.data.id;
    } else {
        await aiMessageVettingAgentsClient.updateAiMessageVettingAgent({ teamId }, props.agentId, agent.value);
    }
};

const scrollToInstructionsCardIfNeeded = () => {
    const element = document.getElementById('instructions');

    if (!element || element.getBoundingClientRect().top >= 0) {
        return;
    }

    window.scrollTo({
        top: element.getBoundingClientRect().top + window.scrollY - 70,
        behavior: 'smooth',
    });
};

const { submit, processing, validation } = useFormSubmission({
    data: agent,
    validation: {
        name: {
            required,
            maxLength: maxLength(80),
            format: groupAndOrgUnitName,
        },
        description: {
            maxLength: maxLength(280),
        },
        instructions: {
            required,
            maxLength: maxLength(3000),
        },
    },
    preserveScroll: () => Boolean(props.agentId),
    submit: save,
    notification: () => ({
        message: props.agentId ? 'Agent updated' : '<strong>New Agent</strong> created successfully.',
        redirect: recentlyCreatedId.value
            ? `/settings/teams/${teamId}/ai-agents/message-vetting/${recentlyCreatedId.value}/usage`
            : undefined,
        action: {
            icon: faSquareArrowUpLeft,
            label: 'Back to Agents',
            href: listPageUrl,
        },
    }),
});
</script>
